import type { CustomerQuoteLine, CustomerQuoteSnapshot, CustomerQuoteSourceChange } from "../types";
import { applyCustomerQuoteAgentCollection, canToggleCustomerQuoteAgentCollection, buildCustomerQuotePricingGroups, calculateCustomerQuoteLinkedQuantity, calculateCustomerQuoteSummary, formatCustomerQuoteCompactDecimal, normalizeCustomerQuoteMoney, recalculateCustomerQuoteTax } from "./draftService";

/** 金额转换为整数分，避免删除利润提示丢失精度。 */
function cents(value: string): bigint {
  const normalized = normalizeCustomerQuoteMoney(value);
  if (normalized === null) throw new Error("金额不合法");
  return BigInt(normalized.replace(".", ""));
}

/** 整数分恢复为两位小数金额。 */
function money(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 数量采用与后端一致的四位定点，非法来源不进入金额试算。 */
function quantity(value: string): bigint {
  const matched = /^(\d+)(?:\.(\d{0,4})0*)?$/.exec(value.trim());
  if (!matched) throw new Error("来源计费数量不合法，请重新检查来源");
  return BigInt(matched[1]) * 10000n + BigInt((matched[2] ?? "").padEnd(4, "0"));
}

/** 万分数量乘整数分，按半上规则取到整数分。 */
function multiply(amount: bigint, count: bigint, cost = 0n): bigint {
  const product = amount * count + cost * 10000n;
  const absolute = product < 0n ? -product : product;
  const rounded = (absolute + 5000n) / 10000n;
  return product < 0n ? -rounded : rounded;
}

/** 金额请求最多两位有效小数，不能先舍入后发送原始非法输入。 */
function markupCents(value: string): bigint {
  if (!/^[+-]?\d+(?:\.\d{0,2}0*)?$/.test(value.trim())) {
    throw new Error("加价必须是最多两位小数的合法金额");
  }
  return cents(value);
}

/** 在完整来源成本上应用单价差，仅在转换凭据和尾差范围均合法时保留尾差。 */
function sourceQuoteAmount(line: CustomerQuoteLine, price: bigint): string {
  const count = quantity(line.quantity);
  const cost = cents(line.costAmount);
  const unit = cents(line.costUnitPrice);
  const conversion = line.sourceEntrySnapshot?.conversion;
  if (conversion && typeof conversion === "object" && !Array.isArray(conversion)) {
    const saved = conversion as Record<string, unknown>;
    try {
      const remainder = cost - multiply(unit, count);
      const absolute = remainder < 0n ? -remainder : remainder;
      if (quantity(String(saved.quantity)) === count
        && quantity(line.sourceQuantity ?? "0") === count
        && cents(String(saved.unitPrice)) === unit && cents(String(saved.amount)) === cost
        && absolute * 20000n <= (count > 10000n ? count : 10000n)) {
        return money(multiply(price - unit, count, cost));
      }
    } catch { /* 不完整转换凭据按正常数量乘单价计算。 */ }
  }
  return money(multiply(price, count));
}

/** 单行/组内角色共用后端基础加价倍率，禁止费用规则不允许的写入。 */
function withMarkup(line: CustomerQuoteLine, markup: bigint): CustomerQuoteLine {
  if ((line.profitPolicy === "none" || line.financeTreatment === "agent_collection") && markup !== 0n) {
    throw new Error("该费用项不允许加价");
  }
  if (line.feeCategory === "tax") {
    return { ...line, quantity: "1", quoteUnitPrice: line.costAmount, quoteAmount: line.costAmount };
  }
  const factor = line.feeCategory === "ticket" && ["teacher", "老师", "教师", "院长"].includes((line.travelerRole ?? "").trim().toLowerCase()) ? 2n : 1n;
  const price = cents(line.costUnitPrice) + markup * factor;
  if (price < 0n) throw new Error("报价单价不能为负数");
  const quoteUnitPrice = money(price);
  const quoteAmount = line.financeTreatment === "agent_collection" ? line.costAmount : sourceQuoteAmount(line, price);
  if (normalizeCustomerQuoteMoney(quoteUnitPrice) === null || normalizeCustomerQuoteMoney(quoteAmount) === null) throw new Error("报价金额超出合法范围");
  return { ...line, quoteUnitPrice, quoteAmount };
}

/** 试算当前变化及明确的定价联动，不推进其他来源快照。 */
export function previewCustomerQuoteSourceChange(snapshot: CustomerQuoteSnapshot, change: CustomerQuoteSourceChange, baseMarkup?: string, sourceChanges: readonly CustomerQuoteSourceChange[] = [], financeTreatment?: CustomerQuoteLine["financeTreatment"]) {
  if (financeTreatment !== undefined && (change.changeType !== "added" || !change.afterLine || !canToggleCustomerQuoteAgentCollection(change.afterLine))) throw new Error("该变化不支持切换收款方式");
  if (change.changeType === "header_modified") throw new Error("参数变化请核对前后值后单独确认");
  if (!change.sourceBudgetLineId) throw new Error("当前来源身份不可用，请重新检查来源");
  const previous = snapshot.lines.find((line) => line.lineOrigin === "budget" && line.sourceBudgetLineId === change.sourceBudgetLineId);
  let next: CustomerQuoteSnapshot;
  let linkedLines: NonNullable<CustomerQuoteSourceChange["linkedLines"]> = [];
  if (change.changeType === "deleted") {
    if (baseMarkup !== undefined) throw new Error("删除项目不能同时设置加价");
    if (!previous) throw new Error("当前来源项目已变化，请重新检查来源");
    next = { ...snapshot, lines: snapshot.lines.filter((line) => line !== previous) };
  } else {
    if (!change.afterLine || change.afterLine.lineOrigin !== "budget" || change.afterLine.sourceBudgetLineId !== change.sourceBudgetLineId) throw new Error("当前来源明细身份不一致，请重新检查来源");
    if ((change.changeType === "added") === Boolean(previous)) throw new Error("当前来源项目已变化，请重新检查来源");
    const usedNumbers = new Set(snapshot.lines.map((line) => line.lineNo));
    const lineNo = previous?.lineNo ?? (usedNumbers.has(change.afterLine.lineNo) ? Math.max(0, ...usedNumbers) + 1 : change.afterLine.lineNo);
    let latest = { ...change.afterLine, lineNo };
    if (financeTreatment !== undefined) latest = applyCustomerQuoteAgentCollection(latest, financeTreatment === "agent_collection");
    if (latest.isLinkedToPeopleCount) {
      // 最新来源人数仅作为计算依据，待确认表头不得写进销售报价快照。
      const sourceCounts = { ...snapshot };
      const fields = { source_student_count: "sourceStudentCount", source_teacher_count: "sourceTeacherCount", source_guide_count: "sourceGuideCount", source_driver_count: "sourceDriverCount" } as const;
      for (const item of sourceChanges) {
        if (item.changeType !== "header_modified" || !item.headerField || !(item.headerField in fields)) continue;
        const value = Number(item.newValue);
        if (item.newValue === null || !Number.isSafeInteger(value) || value < 0) throw new Error("来源人数不可用，请重新检查来源");
        sourceCounts[fields[item.headerField as keyof typeof fields]] = value;
      }
      const count = calculateCustomerQuoteLinkedQuantity(latest, sourceCounts);
      if (count !== null && quantity(count) !== quantity(latest.quantity)) {
        latest = { ...latest, quantity: count, costAmount: money(multiply(cents(latest.costUnitPrice), quantity(count))) };
      }
    }
    const canMarkup = latest.profitPolicy !== "none" && latest.financeTreatment !== "agent_collection";
    if (canMarkup && change.requiresMarkupReset && baseMarkup === undefined) throw new Error("计价方式已变化，请重新设置加价");
    const markup = markupCents(baseMarkup ?? (canMarkup ? change.previousBaseMarkup ?? "0" : "0"));
    const lines = previous ? snapshot.lines.map((line) => line === previous ? latest : line) : [...snapshot.lines, latest];
    const group = buildCustomerQuotePricingGroups(lines, snapshot.pricingRulesVersion).find((candidate) => candidate.lineNos.includes(lineNo));
    const targets = new Set(group?.lineNos ?? [lineNo]);
    linkedLines = (group?.lines ?? []).filter((line) => line.lineNo !== lineNo).map((line) => ({
      sourceBudgetLineId: line.sourceBudgetLineId, lineNo: line.lineNo, itemName: line.customerName,
      markupFactor: line.feeCategory === "ticket" && ["teacher", "老师", "教师", "院长"].includes((line.travelerRole ?? "").trim().toLowerCase()) ? "2" : "1",
      previousBaseMarkup: null,
    }));
    const changed = lines.map((line) => targets.has(line.lineNo) ? withMarkup(line, markup) : line).sort((left, right) => left.lineNo - right.lineNo);
    const stayChanged = snapshot.lines.some((line) => line.feeCategory === "stay" && changed.some((current) => current.sourceBudgetLineId === line.sourceBudgetLineId && current.quoteUnitPrice !== line.quoteUnitPrice));
    next = { ...snapshot, lines: changed, ...(snapshot.stayHighMarkupConfirmed && stayChanged ? { stayHighMarkupConfirmed: false } : {}) };
  }
  if (previous?.feeCategory === "tax" || change.afterLine?.feeCategory === "tax") {
    next = recalculateCustomerQuoteTax(next);
  }
  const summary = calculateCustomerQuoteSummary(next);
  const targetProfit = (cents(summary.internalCostTotalAmount) * 16n + 50n) / 100n;
  const shortfall = targetProfit - cents(summary.expectedGrossProfitAmount);
  const beforeSummary = change.changeType === "deleted" ? calculateCustomerQuoteSummary(snapshot) : null;
  return {
    snapshot: next, summary, linkedLines, targetShortfall: money(shortfall > 0n ? shortfall : 0n),
    removedQuoteAmount: beforeSummary ? money(cents(beforeSummary.customerPaymentTotalAmount) - cents(summary.customerPaymentTotalAmount)) : null,
    removedMarkupAmount: beforeSummary ? money(cents(beforeSummary.expectedGrossProfitAmount) - cents(summary.expectedGrossProfitAmount)) : null,
  };
}

/** 将影响报价判断的具体来源字段转为前后对照。 */
export function customerQuoteSourceComparison(change: CustomerQuoteSourceChange): Array<{ label: string; before: string; after: string }> {
  const fields: Array<[keyof CustomerQuoteLine, string]> = [
    ["sourceItemName", "名称"], ["sourceSpuName", "资源"], ["sourceSkuName", "规格名称"],
    ["sourceItemSpec", "规格"], ["resourceType", "费用类型"], ["travelerRole", "人员角色"],
    ["sourceQuantity", "核价计费数量"], ["quantity", "报价计费数量"], ["sourceManualPeopleCount", "人数"], ["sourceDayCount", "天数"],
    ["sourceNightCount", "晚数"], ["unit", "单位"], ["sourceCalcType", "计费方式"],
    ["sourcePeopleMode", "人数口径"], ["costUnitPrice", "成本单价"], ["sourceCostAmount", "成本合计"],
    ["sourcePriceBasisDesc", "计算依据"], ["sourceMealType", "餐次"],
    ["sourceIsLinkedToPeopleCount", "人数联动"], ["sourceIsCapitaShared", "人均分摊"],
    ["sourceIsAgentCollection", "来源代收"], ["financeTreatment", "收款方式"],
    ["sourcePriceSourceType", "价格来源"],
  ];
  const numericFields = new Set<keyof CustomerQuoteLine>([
    "sourceQuantity", "quantity", "sourceManualPeopleCount", "sourceDayCount", "sourceNightCount",
    "costUnitPrice", "sourceCostAmount",
  ]);
  const numericContextFields = new Set([
    "billingCount", "peopleCount", "manualPeopleCount", "quantity", "unitPrice", "amount",
    "dayCount", "nightCount", "dayNumbers",
  ]);
  const rows: Array<{ label: string; before: string; after: string }> = [];
  const display = (value: unknown): string => value == null || value === "" ? "—" : typeof value === "boolean" ? value ? "是" : "否" : Array.isArray(value) ? value.join("、") : String(value);
  const enumLabels: Record<string, Record<string, string>> = {
    sourceCalcType: { snapshot: "快照计费", business_tax: "系统营业税" },
    sourcePeopleMode: { manual: "手动指定", linked: "随人数联动", student: "学生", teacher: "老师", guide: "导游", driver: "司机", all: "全员" },
    sourcePriceSourceType: { manual: "人工录入", unpriced: "待补价", route_plan_snapshot: "行程价格快照" },
    financeTreatment: { contract_receivable: "合同应收", agent_collection: "代收代付" },
    travelerRole: { student: "学生", teacher: "老师", guide: "导游", driver: "司机", all: "全员" },
    quantityMode: { fixed_people: "固定人数", linked_people: "随角色人数联动", quantity: "数量计费", total: "整项金额" },
    serviceType: { local_charter: "当地用车", pickup: "单接", dropoff: "单送" },
  };
  // 数值只去小数尾零；名称、编码和未知文本保留原样，避免显示格式改变业务身份。
  const displayField = (field: string, value: unknown, numeric = false): string => {
    if (numeric && Array.isArray(value)) return value.map((item) => displayField(field, item, true)).join("、");
    if (numeric && (typeof value === "string" || typeof value === "number") && /^[+-]?\d+(?:\.\d*)?$/.test(String(value).trim())) return formatCustomerQuoteCompactDecimal(String(value).trim());
    return typeof value === "string" ? enumLabels[field]?.[value] ?? display(value) : display(value);
  };
  const comparisonValue = (value: unknown, numeric: boolean): string => {
    if (numeric && Array.isArray(value)) return JSON.stringify(value.map((item) => comparisonValue(item, true)));
    if (numeric && (typeof value === "string" || typeof value === "number")) {
      const matched = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(String(value).trim());
      if (matched) {
        const unsigned = formatCustomerQuoteCompactDecimal(`${BigInt(matched[2])}.${matched[3] ?? ""}`);
        return `decimal:${matched[1] === "-" && unsigned !== "0" ? "-" : ""}${unsigned}`;
      }
    }
    return `text:${display(value)}`;
  };
  for (const [field, label] of fields) {
    const beforeValue = change.beforeLine?.[field];
    const afterValue = change.afterLine?.[field];
    if (comparisonValue(beforeValue, numericFields.has(field)) !== comparisonValue(afterValue, numericFields.has(field))) {
      rows.push({ label, before: displayField(field, beforeValue, numericFields.has(field)), after: displayField(field, afterValue, numericFields.has(field)) });
    }
  }
  const contextLabels: Record<string, string> = {
    areaId: "区域标识", areaName: "区域", regionKey: "区域范围", regionName: "区域名称",
    pointId: "考察点标识", pointName: "考察点", serviceType: "服务类型", dayNumbers: "服务天次",
    vehicleTypeId: "车型标识", vehicleTypeName: "车型", vehicleSpec: "车型规格",
    fromCityCode: "出发地编码", fromCityName: "出发地", toCityCode: "目的地编码", toCityName: "目的地",
    quantityMode: "数量口径", billingCount: "计费次数", groupLabel: "录入组名称",
  };
  const flatten = (value: unknown, path = "", result = new Map<string, unknown>()): Map<string, unknown> => {
    if (Array.isArray(value) && value.some((item) => item !== null && typeof item === "object")) {
      value.forEach((item, index) => flatten(item, `${path}[${index + 1}]`, result));
    } else if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      for (const [key, item] of Object.entries(value)) flatten(item, path ? `${path}.${key}` : key, result);
    } else {
      result.set(path, value);
    }
    return result;
  };
  const beforeContext = flatten(change.beforeLine?.sourceEntrySnapshot ?? {});
  const afterContext = flatten(change.afterLine?.sourceEntrySnapshot ?? {});
  for (const path of new Set([...beforeContext.keys(), ...afterContext.keys()])) {
    // 转换凭据和组 ID 属于技术身份；具体服务范围和业务因素必须逐字段展开。
    if (["conversion", "groupId", "schemaVersion", "inputMode"].some((key) => path === key || path.startsWith(`${key}.`))) continue;
    const before = beforeContext.get(path);
    const after = afterContext.get(path);
    const key = path.replace(/^context\./, "");
    const numeric = numericContextFields.has(path.split(".").at(-1) ?? path);
    if (comparisonValue(before, numeric) !== comparisonValue(after, numeric)) {
      rows.push({ label: contextLabels[key] ?? `服务范围 · ${key}`, before: displayField(key, before, numeric), after: displayField(key, after, numeric) });
    }
  }
  return rows;
}
